import React, { useState, useEffect, useCallback } from 'react';
import api, { fmtDateTimeParis } from '../../lib/api';
import { Power, Sparkles, Plus, Trash2, ChevronUp, ChevronDown, Check, Pencil, PlayCircle, MessageCircle, RefreshCw } from 'lucide-react';

// Panneau du bot HERA rendu NATIVEMENT dans ChatHub (pas de mot de passe).
// Tout passe par le proxy backend /api/bot-panel/* (auth = session ChatHub).
// Objectif : SIMPLE. L'essentiel est visible ; les réglages avancés sont repliés.

const bp = {
  get: (u) => api.get('/bot-panel/' + u).then((r) => r.data),
  post: (u, b) => api.post('/bot-panel/' + u, b || {}).then((r) => r.data),
};

function Toggle({ on, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} className="w-11 h-6 rounded-full transition-colors relative shrink-0 disabled:opacity-50" style={{ background: on ? '#3f7dff' : 'rgba(255,255,255,.14)' }}>
      <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all" style={{ left: on ? '22px' : '2px' }} />
    </button>
  );
}
const Card = ({ title, help, children, right }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
    {title && <div className="flex items-center justify-between mb-1"><h3 className="font-semibold text-white text-[15px]">{title}</h3>{right}</div>}
    {help && <p className="text-[12px] text-slate-400 mb-3">{help}</p>}
    {children}
  </div>
);
// Section repliable pour les réglages avancés (garde le panneau léger).
function Collapse({ title, help, children, open: openDefault = false }) {
  const [open, setOpen] = useState(openDefault);
  // 13/10 (point 6 du brief de la session bot) : un bloc qui demande une action (⚠) s'ouvre tout
  // seul — y compris quand ses données arrivent APRÈS l'affichage. On ne referme jamais à sa place.
  useEffect(() => { if (openDefault) setOpen(true); }, [openDefault]);
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02]">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between p-5 text-left">
        <div><h3 className="font-semibold text-white text-[15px]">{title}</h3>{help && <p className="text-[12px] text-slate-400 mt-0.5">{help}</p>}</div>
        <ChevronDown size={18} className={`text-slate-400 transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-5 pb-5 -mt-1">{children}</div>}
    </div>
  );
}
const input = 'w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-slate-200 outline-none focus:border-indigo-500/40';

// SCRIPTS → FUNNEL (Marlon : « les scripts sont déjà le funnel, rien à réécrire »).
// Choisir un compte ChatHub + un scénario (tag) → le bot reçoit les étapes dans l'ordre.

// Choix A de Marlon (09/09) : il coche les scénarios que le bot a le DROIT d'utiliser.
// Le bot garde sa liberté de conversation, mais ne sortira aucun contenu hors de là,
// et le funnel ne peut être construit que depuis un scénario coché.
function ScriptsAutorises({ botModel, flash, onCoches }) {
  const [comptes, setComptes] = useState([]);
  const [compte, setCompte] = useState('');
  const [dispo, setDispo] = useState([]);
  const [choisis, setChoisis] = useState([]);
  const [chargement, setChargement] = useState(false);

  // 30/09 (Marlon) : « il n'y a rien de coché … c'est pas bien fait, pas clair ». La cause : ce menu
  // laissait choisir un compte SANS rapport avec le modèle sélectionné en haut. L'écran cherchait alors
  // la paire (compte Sara Roux + modèle chloe), qui n'existe pas, et affichait « rien de coché » alors que
  // 7 scénarios étaient enregistrés. Le compte SUIT maintenant le modèle, on ne le choisit plus a la main.
  useEffect(() => {
    api.get('/accounts').then(({ data }) => setComptes(data.accounts || data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!comptes.length) { setCompte(''); return; }
    const norme = (x) => String(x || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const sien = comptes.find((a) => norme(a.name).startsWith(norme(botModel)))
      || comptes.find((a) => norme(botModel).startsWith(norme(a.name)));
    setCompte(sien ? sien.id : '');
    if (!sien && onCoches) onCoches(0);   // aucun compte pour ce modèle : rien n'est coché, on le dit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comptes, botModel]);

  useEffect(() => {
    if (!compte || !botModel) return;
    setChargement(true);
    api.get('/bot-sync/scripts-allowed', { params: { account_id: compte, bot_model: botModel } })
      .then(({ data }) => {
        setDispo(data.tags_disponibles || []); setChoisis(data.tags_autorises || []);
        // 13/10 : le résumé du modèle affiche ce nombre ENREGISTRÉ (pas les cases en cours de clic).
        onCoches && onCoches((data.tags_autorises || []).length);
      })
      .catch(() => {})
      .finally(() => setChargement(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compte, botModel]);

  const basculer = (t) => setChoisis((l) => (l.includes(t) ? l.filter((x) => x !== t) : [...l, t]));

  const enregistrer = async () => {
    try {
      const { data } = await api.post('/bot-sync/scripts-allowed', { account_id: compte, bot_model: botModel, tags: choisis });
      const sc = data.scenarios || {};
      onCoches && onCoches(choisis.length);
      flash(`✓ ${(sc.coches || []).length} scénario(s) utilisables par le bot${(sc.decoches || []).length ? ` · retirés : ${sc.decoches.join(', ')}` : ''}${(sc.erreurs || []).length ? ` · ⚠ ${sc.erreurs.join(' · ')}` : ''}`);
    } catch (e) { flash('⚠ ' + (e?.response?.data?.detail || 'échec')); }
  };

  // 13/10 (point 6) : replié par défaut, ouvert tout seul s'il n'y a rien de coché (⚠).
  const rienDeCoche = !chargement && !!compte && dispo.length > 0 && choisis.length === 0;
  return (
    <Collapse open={rienDeCoche}
      title={`Scénarios que le bot peut utiliser — ${chargement ? '…' : rienDeCoche ? '⚠ rien de coché' : `✓ ${choisis.length} coché${choisis.length > 1 ? 's' : ''}`}`}
      help="Coche tous les scénarios que le bot peut utiliser. Pour chaque fan, il choisit lui-même : il finit le scénario en cours, puis passe à un scénario coché qu'il n'a jamais fait avec ce fan. Il ne renvoie jamais un contenu déjà envoyé (par lui ou par une chatteuse), et un lien non payé est reproposé plus tard, après avoir bien rechauffé le fan. Pas de « finale » à choisir : en mode A→Z, il enchaîne tout seul.">
      {/* 21/09 : le bouton du bas crée UN script ; ces cases pilotent TOUS les scénarios du bot */}
      <div className="mb-3 text-[13px]">
        {compte ? (
          <span className="text-slate-300">
            Scénarios du compte Telegram <b className="text-white">{(comptes.find((a) => a.id === compte) || {}).name}</b>
            <span className="text-slate-500"> — lié au modèle choisi en haut de la page.</span>
          </span>
        ) : (
          <span className="text-amber-300">
            Aucun compte Telegram ne correspond au modèle choisi en haut de la page.
          </span>
        )}
      </div>
      {chargement ? <div className="text-sm text-slate-500 py-2">Chargement…</div> : dispo.length === 0 ? (
        <div className="text-sm text-slate-500 py-2">Aucun scénario de scripts sur ce compte.</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {dispo.map((t) => (
              <button key={t} onClick={() => basculer(t)} data-testid={`tag-${t}`}
                className={`px-3 py-1.5 rounded-xl text-sm border transition-colors ${choisis.includes(t)
                  ? 'border-emerald-400/60 bg-emerald-500/15 text-emerald-200'
                  : 'border-white/10 bg-white/[0.03] text-slate-400 hover:border-white/25'}`}>
                {choisis.includes(t) ? '✓ ' : ''}{t}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <button onClick={enregistrer} className="rounded-xl px-3 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500">Enregistrer</button>
            <span className="text-[11px] text-slate-500">
              {choisis.length === 0
                ? '⚠ Rien de coché pour ce modèle : le bot n\'utilisera AUCUN script, il parlera seulement avec sa personnalité — donc il ne vendra rien.'
                : `${choisis.length} scénario${choisis.length > 1 ? 's' : ''} autorisé${choisis.length > 1 ? 's' : ''}.`}
            </span>
          </div>
        </>
      )}
    </Collapse>
  );
}

function ScriptFunnelSync({ botModel, onDone, flash }) {
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState('');
  const [tags, setTags] = useState([]);
  const [tag, setTag] = useState('');
  const [activate, setActivate] = useState(true);
  const [busy, setBusy] = useState(false);
  const [busyBrain, setBusyBrain] = useState(false);
  useEffect(() => {
    api.get('/accounts').then(({ data }) => { setAccounts(data || []); if (data?.[0]) setAccount(data[0].id); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!account) { setTags([]); return; }
    api.get('/scripts', { params: { account_id: account } }).then(({ data }) => {
      const arr = Array.isArray(data) ? data : (data.scripts || []);
      const t = Array.from(new Set(arr.map(s => s.tag).filter(Boolean))).sort();
      setTags(t); setTag(t[0] || '');
    }).catch(() => setTags([]));
  }, [account]);
  const sync = async () => {
    if (!tag) return;
    setBusy(true);
    try {
      const { data } = await api.post('/bot-sync/funnel', { account_id: account, tag, bot_model: botModel, activate });
      flash(`✓ funnel « ${data.funnel} » créé (${data.etapes} étapes${data.actif ? ', actif' : ''})`);
      if (data.etapes_avec_media_a_completer?.length) {
        window.alert(`Funnel créé ✅\n\n⚠️ ${data.etapes_avec_media_a_completer.length} étape(s) avaient un média dans le script — la photo n'est pas jointe automatiquement. Si besoin, ajoute-les dans « Modifier » le funnel :\n- ${data.etapes_avec_media_a_completer.join('\n- ')}`);
      }
      onDone && onDone();
    } catch (e) { flash('⚠ ' + (e?.response?.data?.detail || 'échec de la synchro')); }
    finally { setBusy(false); }
  };
  const brain = async () => {
    setBusyBrain(true);
    try {
      const { data } = await api.post('/bot-sync/brain', { bot_model: botModel });
      flash(`✓ méthode + ${data.exemples_injectes} séquence(s) gagnante(s) injectées dans la persona`);
    } catch (e) { flash('⚠ ' + (e?.response?.data?.detail || 'échec de l\'injection')); }
    finally { setBusyBrain(false); }
  };
  return (
    // 13/10 (point 6) : ne sert qu'une fois par scénario → replié par défaut.
    <Collapse title="🎬 Funnel depuis tes scripts" help="Tes scénarios de scripts (ex. « lingerie noire ») deviennent le funnel du bot — textes, liens et prix déjà prêts, rien à réécrire.">
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="block text-[12px] text-slate-400 mb-1">Compte ChatHub</label>
          <select value={account} onChange={(e) => setAccount(e.target.value)} className={input + ' min-w-[160px]'}>
            {accounts.map(a => <option key={a.id} value={a.id} className="bg-[#0B0B0E]">{a.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[12px] text-slate-400 mb-1">Scénario (tag de scripts)</label>
          <select value={tag} onChange={(e) => setTag(e.target.value)} className={input + ' min-w-[180px]'}>
            {tags.map(t => <option key={t} value={t} className="bg-[#0B0B0E]">{t}</option>)}
            {!tags.length && <option className="bg-[#0B0B0E]" value="">aucun scénario</option>}
          </select>
        </div>
        <label className="flex items-center gap-1.5 text-[12px] text-slate-300 pb-2.5">
          <input type="checkbox" checked={activate} onChange={(e) => setActivate(e.target.checked)} className="accent-indigo-500" /> activer directement
        </label>
        <button onClick={sync} disabled={busy || !tag}
          className="rounded-xl px-3 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: 'linear-gradient(135deg,#3f7dff,#73a6ff)' }}>
          {busy ? 'Création…' : 'Créer le funnel depuis ce scénario'}
        </button>
      </div>
      <div className="mt-3 pt-3 border-t border-white/5 text-[11px] text-slate-500">
        {/* 12/10 — ce bloc annonçait une réinjection automatique quotidienne. C'est FAUX
            depuis que Marlon a fait bloquer le cerveau ChatHub (B-757, B-758) : il réécrivait
            les exemples de la persona avec les phrases des fans. */}
        La personnalité du bot n'est <b className="text-slate-400">plus mise à jour
        automatiquement</b> depuis ChatHub (bloqué le 12/10). Ce bouton crée le funnel à partir
        du scénario, et rien d'autre n'est réinjecté.
      </div>
    </Collapse>
  );
}

export default function BotPanel({ modeleImpose = '' } = {}) {
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState('');
  const [S, setS] = useState(null);
  const [FUN, setFUN] = useState({ funnels: [], activeFunnelId: null });
  const [voirModes, setVoirModes] = useState(false);   // 13/10 (point 6) : choix du mode replié
  const [nbCoches, setNbCoches] = useState(null);      // 13/10 : scénarios cochés, pour le résumé
  useEffect(() => { setNbCoches(null); }, [modelId]);  // pas le chiffre du modèle d'avant
  // Combien d'etapes de contenu du scenario ACTIF portent une description (`contenuReel`).
  const DECRITS = (() => {
    const f = (FUN.funnels || []).find((x) => x.id === FUN.activeFunnelId) || (FUN.funnels || [])[0];
    const etapes = ((f || {}).steps || []).filter((st) => st && (st.linkUrl || st.photoFileId || st.vaultId));
    const faits = etapes.filter((st) => String(st.contenuReel || '').trim().length > 12).length;
    return { total: etapes.length, faits, manque: etapes.length - faits };
  })();
  const [fans, setFans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  // B-392 (04/10) : les fans que le BOT a classes time waster tout seul, pour que Marlon me controle.
  // tw = null tant qu'on n'a pas charge (on n'affiche rien), [] = charge et vide.
  const [tw, setTw] = useState(null);
  // 12/10 : la liste des time wasters est repliee par defaut (56 lignes), filtrable par
  // modele, et ne montre que 10 lignes a la fois.
  const [twOuvert, setTwOuvert] = useState(false);
  const [twCompte, setTwCompte] = useState('');
  const [twVus, setTwVus] = useState(10);
  // Les classements a afficher : filtres par modele si Marlon en a choisi un, et les
  // « ancienne regle » / « bot d'accueil » rejetes en fin de liste pour qu'ils ne noient
  // pas les classements recents du bot HERA (demande du 12/10).
  const twListe = (tw || [])
    .filter((f) => !twCompte || f.compte === twCompte)
    .slice()
    .sort((a, b) => {
      const vieux = (f) => (f.par !== 'hera' ? 2 : /\b2 refus\b/i.test(String(f.raison || '')) ? 1 : 0);
      return vieux(a) - vieux(b);
    });
  const [twEnCours, setTwEnCours] = useState('');
  const [twFil, setTwFil] = useState({ id: '', messages: [], charge: false, erreur: '' });

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(''), 2500); };
  const mq = useCallback(() => '?model=' + encodeURIComponent(modelId), [modelId]);

  const loadFans = useCallback(async () => {
    const list = await bp.get('fans?list=all').catch(() => []);
    setFans(Array.isArray(list) ? list : []);
  }, []);

  // B-392 : la liste des classements time waster (tous comptes), la plus recente d'abord.
  const chargerTW = useCallback(async () => {
    try {
      const { data } = await api.get('/hera/time-wasters');
      setTw(Array.isArray(data?.fans) ? data.fans : []);
    } catch { setTw([]); }
  }, []);

  // « Pas un time waster » : j'ai eu tort -> le classement est retire, le fan debloque, et il repart
  // aux CHATTEUSES (pas au bot : c'est le bot qui s'est trompe sur lui).
  const annulerTW = async (f) => {
    if (!window.confirm(`Annuler le classement « time waster » de ${f.nom} ?

Il sera débloqué et rendu aux chatteuses. Le bot ne lui ecrira plus de lui-meme ; pour le remettre au
bot, utilise « Confier au bot » depuis sa conversation.`)) return;
    setTwEnCours(f.conversation_id);
    try {
      await api.post(`/hera/time-wasters/${f.conversation_id}/annuler`);
      flash(`\u2713 ${f.nom} n'est plus time waster — rendu aux chatteuses`);
    } catch (e) {
      flash('\u26a0 ' + (e?.response?.data?.detail || 'échec'));
    } finally {
      setTwEnCours('');
      if (twFil.id === f.conversation_id) setTwFil({ id: '', messages: [], charge: false, erreur: '' });
      chargerTW();
    }
  };

  // Juger un classement sans lire la conversation est impossible : on ouvre le fil en lecture seule,
  // sur place (le fan n'est dans aucune liste, il est bloque). `peek` n'ecrit aucun « vu ».
  const ouvrirTW = async (f) => {
    const id = f.conversation_id;
    if (twFil.id === id) { setTwFil({ id: '', messages: [], charge: false, erreur: '' }); return; }
    setTwFil({ id, messages: [], charge: true, erreur: '' });
    try {
      const { data } = await api.get(`/conversations/${id}/messages`, { params: { peek: true } });
      setTwFil((o) => (o.id === id ? { ...o, messages: Array.isArray(data) ? data : [], charge: false } : o));
    } catch {
      setTwFil((o) => (o.id === id ? { ...o, charge: false, erreur: 'Conversation impossible à charger.' } : o));
    }
  };

  const loadModel = useCallback(async (id) => {
    const s = await bp.get('settings?model=' + encodeURIComponent(id)).catch(() => null);
    setS(s);
    const f = await bp.get('funnels?model=' + encodeURIComponent(id)).catch(() => ({ funnels: [], activeFunnelId: null }));
    setFUN(f); setEditing(null);
    loadFans();
  }, [loadFans]);

  const init = useCallback(async () => {
    const r = await bp.get('models').catch(() => ({ models: [] }));
    setModels(r.models || []);
    const id = (r.models || []).find((m) => m.id === modelId)?.id || (r.models || [])[0]?.id || '';
    setModelId(id);
    if (id) await loadModel(id);
  }, [modelId, loadModel]);
  useEffect(() => { init(); chargerTW(); /* eslint-disable-next-line */ }, []);

  const switchModel = async (id) => { setModelId(id); await loadModel(id); };
  // 21/09 : « Régler Sara ↓ » dans la vue d'ensemble ouvre directement ses reglages ici.
  useEffect(() => {
    if (modeleImpose && modeleImpose !== modelId) switchModel(modeleImpose);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modeleImpose]);
  const saveSettings = async (patch) => { const s = await bp.post('settings' + mq(), patch); setS(s); return s; };

  const addModel = async () => {
    const name = window.prompt('Nom du nouveau modèle (ex : Sara, Chloé) :'); if (!name) return;
    await bp.post('models', { name });
    await init();
    window.alert(`Modèle « ${name} » créé ✅\n\nPour qu'il fonctionne : sur le Telegram de ${name} (compte Premium), va dans Réglages → Telegram Business → Chatbots et ajoute @lenachatmodelbot. Le compte se reliera tout seul. Ensuite règle sa persona et son funnel ici.`);
  };
  const renameModel = async () => {
    const name = window.prompt('Nouveau nom :', models.find((m) => m.id === modelId)?.name || ''); if (!name) return;
    await bp.post('models', { id: modelId, name }); await init();
  };

  const genPersona = async () => {
    setBusy(true); setMsg('génération de la persona… (quelques secondes)');
    try {
      await saveSettings({ brief: S.brief || '' });
      const r = await bp.post('persona/generate' + mq(), { brief: S.brief || '' });
      if (r.persona) { setS((x) => ({ ...x, persona: r.persona })); flash('✓ persona générée — relis puis Enregistre'); }
      else flash('⚠ ' + (r.error || 'échec'));
    } catch (e) { flash('⚠ ' + (e?.response?.data?.detail || e.message)); } finally { setBusy(false); }
  };
  const savePersona = async () => { const r = await bp.post('persona' + mq(), { persona: S.persona }); setS((x) => ({ ...x, persona: r.persona })); flash('✓ persona enregistrée'); };

  // --- Fans : activer/couper le bot PAR conversation ---
  // 20/09 : simule le paiement du lien EN ATTENTE de ce fan (route POST /api/fan/:id/sim-pay du bot).
  // Pas besoin de retrouver ni de coller l'identifiant Dropp : le test suit exactement le chemin
  // d'un vrai paiement (meme handler `onPayment`).
  const simulerPaiement = async (f) => {
    if (!window.confirm(`Faire comme si ${f.name || f.telegramId} venait de payer son lien ?

C'est un TEST : aucun argent n'est encaisse, mais le bot va vraiment continuer la conversation sur Telegram.`)) return;
    try {
      const r = await bp.post(`fan/${f.id}/sim-pay`, {});
      flash(r.ok ? `✓ paiement simule (${r.amount}€) — regarde Telegram` : '⚠ ' + (r.error || 'échec'));
      loadFans();
    } catch (e) { flash('⚠ ' + (e?.response?.data?.detail || e?.response?.data?.error || e.message)); }
  };

  const toggleFan = async (id, active) => {
    setFans((list) => list.map((f) => (f.id === id ? { ...f, botActive: active } : f)));
    try { await bp.post(`fan/${id}/bot`, { active }); flash(active ? '✓ bot activé pour ce fan' : '✓ bot coupé pour ce fan'); }
    catch { setFans((list) => list.map((f) => (f.id === id ? { ...f, botActive: !active } : f))); flash('⚠ échec'); }
  };

  // --- Funnels ---
  // Modèle GUIDÉ : structure recommandée pour la 1ʳᵉ vente. Le bot chauffe d'abord (persona),
  // puis envoie cette offre ; s'il n'a pas payé il relance, dès qu'il paie → passage chatteur.
  const editFunnel = (f) => setEditing(JSON.parse(JSON.stringify(f)));
  const saveFunnel = async () => { const f = await bp.post('funnels' + mq(), editing); setFUN(f); setEditing(null); flash('✓ funnel enregistré'); };
  const activateF = async (id) => setFUN(await bp.post('funnels/activate' + mq(), { id }));
  const delF = async (id) => { if (!window.confirm('Supprimer ce funnel ?')) return; setFUN(await bp.post('funnels/delete' + mq(), { id })); };
  const setStep = (i, k, v) => setEditing((e) => { const s = [...e.steps]; s[i] = { ...s[i], [k]: v }; return { ...e, steps: s }; });
  const addStep = () => setEditing((e) => ({ ...e, steps: [...e.steps, { sellMessage: '', contenuReel: '', linkUrl: '', photoFileId: '', afterSaleMessage: '' }] }));
  const rmStep = (i) => setEditing((e) => ({ ...e, steps: e.steps.filter((_, j) => j !== i) }));
  const mvStep = (i, d) => setEditing((e) => { const j = i + d; if (j < 0 || j >= e.steps.length) return e; const s = [...e.steps]; [s[i], s[j]] = [s[j], s[i]]; return { ...e, steps: s }; });
  const upPhoto = async (i, file) => {
    if (!file) return;
    const b64 = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(file); });
    try { const r = await bp.post('funnel/photo' + mq(), { imageBase64: b64, filename: file.name }); if (r.fileId) { setStep(i, 'photoFileId', r.fileId); flash('✓ photo ajoutée'); } else flash('⚠ ' + (r.error || 'échec photo')); }
    catch (e) { flash('⚠ ' + (e?.response?.data?.detail || 'photo: modèle non connecté ?')); }
  };

  if (!S && models.length === 0) return <div className="text-slate-400 text-sm p-2">Chargement du panneau…</div>;

  const modelName = (models.find((m) => m.id === modelId) || {}).name || '';
  // Deux pièges qui font croire que « le bot ne marche pas » : la whitelist de test
  // (il ne répond qu'à ces numéros) et un funnel actif sans le moindre visuel.
  const wl = (S?.testWhitelist || []).filter(Boolean);
  const funnelActif = (FUN?.funnels || []).find((f) => f.enabled);
  const etapesFunnel = (funnelActif?.steps || []).length;
  const sansPhoto = (funnelActif?.steps || []).filter((st) => !st.photoFileId).length;
  const visibleFans = fans.filter((f) => !f.modele || !modelName || String(f.modele).toLowerCase() === modelName.toLowerCase());

  return (
    <div className="space-y-4">
      {msg && <div className="sticky top-0 z-10 rounded-xl border border-indigo-500/30 bg-indigo-500/10 px-4 py-2 text-sm text-indigo-100">{msg}</div>}

      {S?.botEnabled && wl.length > 0 && (
        <div data-testid="bot-alerte-whitelist" className="rounded-xl border border-amber-500/40 bg-amber-500/[0.08] px-4 py-3 text-[13px] text-amber-100">
          <b>Mode test — le bot ne répond qu'à {wl.length} numéro{wl.length > 1 ? 's' : ''}</b> ({wl.join(', ')}).
          <span className="text-amber-200/80"> Tout autre fan qui écrit n'aura aucune réponse du bot.
          Pour ouvrir à tous, vide la liste dans « Limiter qui le bot contacte » plus bas.</span>
        </div>
      )}



      {/* B-392 (04/10) — « je pourrais passer derriere toi et verifier si tu as bien fait ».
          Les classements que le BOT a poses seul (3 refus nets = time waster) avec leur raison. */}
      {tw && tw.length > 0 && (
        <Card
          title="Time wasters — ce que le bot a décidé tout seul"
          help="Le bot les classe après 3 refus nets de payer (règle du 09/10). Les classements marqués « ancienne règle » datent d'avant et ont été posés après 2 refus — relis-les en priorité. Ils sont bloqués et cachés partout : lis la raison, ouvre la conversation, et annule si je me suis trompé."
          right={<span className="text-[12px] text-slate-400" data-testid="bot-tw-nb">{tw.filter((f) => f.par === 'hera').length} par le bot HERA / {tw.length} au total</span>}
        >
          {/* 12/10, demande de Marlon : 56 lignes à faire défiler, et ça grossit. Fermée par
              défaut, 10 lignes à l'ouverture puis « voir plus », et un filtre par modèle. */}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <button type="button" onClick={() => setTwOuvert((v) => !v)} data-testid="bot-tw-replier"
              className="text-[12px] px-2.5 py-1 rounded-md border border-white/10 text-slate-300 hover:text-white hover:bg-white/5">
              {twOuvert ? '▾ Replier la liste' : `▸ Voir la liste (${tw.length})`}
            </button>
            {twOuvert && (
              <select value={twCompte} onChange={(e) => { setTwCompte(e.target.value); setTwVus(10); }}
                data-testid="bot-tw-filtre-compte"
                className="text-[12px] rounded-md border border-white/10 bg-white/5 text-slate-300 px-2 py-1">
                <option value="">Tous les modèles</option>
                {[...new Set(tw.map((f) => f.compte).filter(Boolean))].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            )}
          </div>
          {twOuvert && (
          <div className="rounded-xl border border-white/10 overflow-hidden">
            {twListe.slice(0, twVus).map((f) => (
              <div key={f.conversation_id} data-testid={`bot-tw-${f.conversation_id}`} className="border-b border-white/5 last:border-0">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-[13px] text-slate-200">
                  <span className="font-semibold truncate max-w-[180px]">{f.nom}</span>
                  <span className="text-[12px] text-slate-400">{f.compte}</span>
                  <span className="text-[12px] text-slate-400">{fmtDateTimeParis(f.classe_le) || '\u2014'}</span>
                  <span className="text-[12px] text-slate-300" data-testid={`bot-tw-depense-${f.conversation_id}`}>
                    dépensé {Number(f.spent || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} &euro;
                  </span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border ${f.par === 'hera' ? 'bg-indigo-500/15 text-indigo-100 border-indigo-400/40' : 'bg-white/5 text-slate-400 border-white/10'}`}
                    data-testid={`bot-tw-par-${f.conversation_id}`}>
                    {f.par === 'hera' ? 'classé par le bot HERA' : 'classé par le bot d\u2019accueil'}
                  </span>
                  {f.encore_au_bot && <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-100 border border-amber-400/40">encore dans la zone bot</span>}
                  {/* 12/10 : 5 classements portent « 2 refus » — ils sont d'avant la décision de
                      Marlon du 09/10 (remise à 3 refus, B-593). On les signale, on n'annule rien. */}
                  {/\b2 refus\b/i.test(String(f.raison || '')) && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-100 border border-rose-400/40"
                      data-testid={`bot-tw-ancienne-regle-${f.conversation_id}`}
                      title="Classé avec l'ancienne règle de 2 refus, avant la décision du 09/10">ancienne règle (2 refus)</span>
                  )}
                  <span className="basis-full text-[12px] text-rose-200/90" data-testid={`bot-tw-raison-${f.conversation_id}`}>
                    &#128308; {f.raison || 'aucune raison enregistrée'}
                  </span>
                  <span className="basis-full flex flex-wrap items-center justify-end gap-2">
                    <button type="button" onClick={() => ouvrirTW(f)} data-testid={`bot-tw-ouvrir-${f.conversation_id}`}
                      className="text-[12px] px-2.5 py-1 rounded-md border border-white/10 text-slate-300 hover:text-white hover:bg-white/5">
                      {twFil.id === f.conversation_id ? 'Fermer' : 'Ouvrir la conversation'}
                    </button>
                    <button type="button" onClick={() => annulerTW(f)} disabled={twEnCours === f.conversation_id}
                      data-testid={`bot-tw-annuler-${f.conversation_id}`}
                      className="text-[12px] px-2.5 py-1 rounded-md border border-emerald-400/40 bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/25 disabled:opacity-40">
                      {twEnCours === f.conversation_id ? '\u2026' : 'Pas un time waster'}
                    </button>
                  </span>
                </div>
                {twFil.id === f.conversation_id && (
                  <div className="mx-3 mb-3 max-h-72 overflow-y-auto rounded-lg border border-white/10 bg-black/30 p-2 space-y-1" data-testid={`bot-tw-fil-${f.conversation_id}`}>
                    {twFil.charge ? <div className="text-[12px] text-slate-400">Chargement&hellip;</div>
                      : twFil.erreur ? <div className="text-[12px] text-red-300">{twFil.erreur}</div>
                        : !twFil.messages.length ? <div className="text-[12px] text-slate-400">Aucun message.</div>
                          : twFil.messages.map((m, i) => (
                            <div key={m.id || i} className={`flex ${m.from_ === 'them' ? 'justify-start' : 'justify-end'}`}>
                              <span className={`max-w-[80%] rounded-lg px-2 py-1 text-[12px] whitespace-pre-wrap break-words ${m.from_ === 'them' ? 'bg-white/10 text-slate-100' : 'bg-indigo-500/20 text-indigo-100'}`}>{m.text || '(média)'}</span>
                            </div>
                          ))}
                    <div className="text-[11px] text-slate-500 pt-1">Lecture seule. Pour lui reparler : « Pas un time waster ».</div>
                  </div>
                )}
              </div>
            ))}
          </div>
          )}
          {twOuvert && twVus < twListe.length && (
            <button type="button" onClick={() => setTwVus((n) => n + 20)} data-testid="bot-tw-voir-plus"
              className="mt-2 w-full rounded-lg border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] px-3 py-1.5 text-[12px] text-slate-200">
              Voir plus ({twListe.length - twVus} restant{twListe.length - twVus > 1 ? 's' : ''})
            </button>
          )}
        </Card>
      )}

      {/* 1. Modèle */}
      <Card title="Modèle" help={`Tes comptes Telegram de ChatHub, repris automatiquement. Chacun a sa persona, ses scénarios autorisés et son funnel.`}>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={modelId} onChange={(e) => switchModel(e.target.value)} className={input + ' flex-1 min-w-[180px]'}>
            {models.map((m) => <option key={m.id} value={m.id}>{m.name}{m.connected ? '' : ' (non connecté)'}</option>)}
          </select>
          <button onClick={renameModel} className="rounded-xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] px-3 py-2 text-sm text-white flex items-center gap-1"><Pencil size={14} /> Renommer</button>
        </div>
        {S && <p className="text-[12px] mt-2" style={{ color: S.connected ? '#37d39a' : '#ffcf6b' }}>{S.connected ? '✓ compte relié à Telegram' : '⚠ pas encore relié — ajoute @lenachatmodelbot dans le Telegram Business de ce modèle.'}</p>}
        {/* 13/10 (point 6 du brief de la session bot) : LE MODÈLE EN UNE LIGNE, avant tout le reste.
            Tout ce qui suit est replié ; on voit ici l'état sans rien ouvrir. */}
        {S && (
          <div data-testid="bot-resume-modele" className="mt-3 rounded-xl border border-white/[0.07] bg-white/[0.02] px-3 py-2 text-[12px] text-slate-300 flex flex-wrap gap-x-2 gap-y-1">
            <span className={S.botEnabled !== false ? 'text-emerald-300' : 'text-slate-400'}>{S.botEnabled !== false ? '● activé' : '○ en pause'}</span>
            <span className="text-slate-600">·</span>
            <span>{S.mode === 'full' ? (S.handoffAfterSale === false ? 'A→Z' : 'accueil + 1ʳᵉ vente') : 'accueil seulement'}</span>
            <span className="text-slate-600">·</span>
            <span>{(S.testWhitelist || []).length ? `${(S.testWhitelist || []).length} fan(s) seulement` : S.onlyNewFans ? `nouveaux + ${(S.toujoursSuivis || []).length} suivi(s)` : <b className="text-amber-300">tout le monde</b>}</span>
            <span className="text-slate-600">·</span>
            {/* 13/10 : « 4 scénarios » ici contre « ✓ 1 coché » juste dessous — deux chiffres qui ne se
                recoupaient pas (funnels existants vs scénarios cochés). Le brief demande les COCHÉS. */}
            <span className={nbCoches === 0 ? 'text-amber-300' : ''}>{nbCoches === null ? '… scénarios' : nbCoches === 0 ? '⚠ aucun scénario coché' : `${nbCoches} scénario${nbCoches > 1 ? 's' : ''} coché${nbCoches > 1 ? 's' : ''}`}</span>
            {DECRITS.total > 0 && (<>
              <span className="text-slate-600">·</span>
              <span className={DECRITS.manque > 0 ? 'text-amber-300' : ''}>{DECRITS.manque > 0 ? '⚠ ' : ''}{DECRITS.faits}/{DECRITS.total} étapes décrites</span>
            </>)}
          </div>
        )}
        {/* 20/09 — Ce modèle est-il VRAIMENT prêt ? Sans persona, le bot se taisait sans rien dire
            (avant, pire : il répondait avec l'identité de Léna). Ces quatre pastilles évitent
            d'allumer un modèle à moitié configuré et de s'en apercevoir en lisant la conversation. */}
        {S && (
          <div data-testid="bot-pastilles-pret" className="mt-3 flex flex-wrap gap-2">
            {[
              { t: 'Persona', ok: !!(S.persona && (S.persona.fiche || S.persona.voix)), ko: 'sans fiche, le bot NE RÉPOND PAS' },
              { t: 'Scénario', ok: !!(FUN?.funnels || []).find((f) => f.id === FUN?.activeFunnelId)?.steps?.length, ko: 'pousse ta série « Bienvenue » ci-dessous' },
              { t: 'Telegram', ok: !!S.connected, ko: 'ajoute le bot dans son Telegram Business' },
              { t: 'Bot activé', ok: S.botEnabled !== false, ko: 'interrupteur en pause' },
            ].map((p) => (
              <span key={p.t} title={p.ok ? '' : p.ko}
                className={`text-[11px] px-2 py-1 rounded-lg border ${p.ok ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' : 'border-amber-500/40 bg-amber-500/10 text-amber-200'}`}>
                {p.ok ? '✓' : '⚠'} {p.t}{p.ok ? '' : ` — ${p.ko}`}
              </span>
            ))}
          </div>
        )}
      </Card>

      {!S ? <Card>Réglages indisponibles pour ce modèle.</Card> : (
        <>
          {/* 2. ESSENTIEL : le bot répond ? + comment */}
          <Card title="Le bot répond aux fans" help="Interrupteur principal pour ce modèle. Coupe-le et tout revient aux chatteurs (les messages restent enregistrés)."
            right={<Toggle on={S.botEnabled} onClick={() => saveSettings({ botEnabled: !S.botEnabled })} />}>
            <div className="flex items-center gap-2 text-sm mb-4">
              <Power size={15} className={S.botEnabled ? 'text-emerald-400' : 'text-slate-500'} />
              <span className="text-slate-300">{S.botEnabled ? 'Activé — le bot prend les nouvelles conversations.' : 'En pause — tout va aux chatteurs.'}</span>
            </div>
            {/* 13/10 (point 6) : le choix du mode est replié ; on lit le mode actuel sans ouvrir. */}
            <button onClick={() => setVoirModes((v) => !v)} className="w-full flex items-center justify-between text-left text-[12px] text-slate-400 mb-2">
              <span>Jusqu'où va le bot ? <b className="text-slate-200">{S.mode === 'full' ? (S.handoffAfterSale === false ? 'Le bot gère tout, de A à Z' : 'Accueil + 1ʳᵉ vente, puis un chatteur') : 'Accueil seulement'}</b></span>
              <span className="text-slate-500 flex items-center gap-1">{voirModes ? 'masquer' : 'changer'} <ChevronDown size={14} className={`transition-transform ${voirModes ? 'rotate-180' : ''}`} /></span>
            </button>
            {voirModes && <div className="space-y-2">
              {[
                // 20/09 (Marlon) : « on va juste garder deux modes ». Le 3e (accueil sans vendre)
                // est retire. Les deux restants posent aussi scriptSouple:true — c'est le
                // comportement valide le 20/09 (le bot discute et place ses contenus quand ca s'y
                // prete, au lieu de reciter le script), pas une option a regler soi-meme.
                { key: 'sale_handoff', t: 'Accueil + 1ʳᵉ vente, puis un chatteur', d: "Il accueille, chauffe, envoie le 1er lien. Dès que le fan A PAYÉ, un chatteur prend le relais.", sel: S.mode === 'full' && S.handoffAfterSale !== false, patch: { mode: 'full', handoffAfterSale: true, scriptSouple: true } },
                { key: 'sale_all', t: 'Le bot gère tout, de A à Z', d: "Il vend, continue après chaque paiement en montant les paliers, relance le fan s'il se tait, et garde ses infos dans ses notes.", sel: S.mode === 'full' && S.handoffAfterSale === false, patch: { mode: 'full', handoffAfterSale: false, scriptSouple: true } },
              ].map((o) => (
                <button key={o.key} onClick={() => saveSettings(o.patch)} className={`w-full text-left p-3 rounded-xl border ${o.sel ? 'border-indigo-500 ring-2 ring-indigo-500/30' : 'border-white/10'} bg-white/[0.03]`}>
                  <div className="text-sm text-white font-semibold flex items-center gap-2">{o.sel && <span className="w-2 h-2 rounded-full bg-indigo-400" />}{o.t}</div>
                  <div className="text-[12px] text-slate-400 mt-0.5">{o.d}</div>
                </button>
              ))}
            </div>}
          </Card>

          {/* 30/09 (Marlon) : « la partie conversation activer/couper le bot … alors que tout en haut
              il y a ce que le bot fait en ce moment. Ca peut confondre un peu. » Cette carte refaisait,
              en moins bien, le bloc « Ce que le bot fait en ce moment » du haut de la page : meme liste
              de fans, meme interrupteur. Elle est retiree — le pilotage fan par fan se fait la-haut,
              ou l'on voit AUSSI le dernier message, qui attend, et ou en est chaque scenario. */}

          {/* 4. Qui le bot engage (test / nouveaux) — replié */}
          {/* 30/09 : refaite pour qu'on comprenne QUI le bot prend, sans lire le code. */}
          <Collapse title={`À qui le bot parle — ${(S.testWhitelist || []).length ? `${(S.testWhitelist || []).length} fan(s) seulement` : S.onlyNewFans ? `les nouveaux + ${(S.toujoursSuivis || []).length} fan(s) nommé(s)` : 'tout le monde'}`}
            help="Deux réglages, et c'est TOUT ce qui décide si le bot répond à quelqu'un.">
            <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3 text-[13px] text-slate-300 mb-3">
              <b className="text-white">Aujourd'hui, ce modèle :</b>{' '}
              {(S.testWhitelist || []).length
                ? <>le bot répond <b className="text-white">uniquement aux {(S.testWhitelist || []).length} fans de la liste fermée</b> ci-dessous. Les nouveaux fans ne sont <b>pas</b> pris.</>
                : S.onlyNewFans
                  ? <>le bot prend <b className="text-white">tous les nouveaux fans</b>, plus les <b className="text-white">{(S.toujoursSuivis || []).length} fans nommément suivis</b>. Les autres anciens restent aux chatteuses.</>
                  : <>le bot répond à <b className="text-amber-300">TOUS les fans</b> qui écrivent, anciens compris.</>}
            </div>

            <label className="block text-[12px] text-slate-400 mb-1">
              <b className="text-slate-300">Liste fermée</b> — si elle n'est pas vide, le bot ne parle QU'à ces numéros Telegram (virgules). Vide = il n'y a pas de restriction ici.
            </label>
            <input defaultValue={(S.testWhitelist || []).join(',')} onBlur={(e) => saveSettings({ testWhitelist: e.target.value }).then(() => flash('✓ enregistré'))} placeholder="ex : 8385657182" className={input} />

            <label className="flex items-center gap-2 text-sm text-slate-300 mt-3 cursor-pointer">
              <input type="checkbox" checked={!!S.onlyNewFans} onChange={(e) => saveSettings({ onlyNewFans: e.target.checked })} className="accent-indigo-500" />
              Nouveaux fans uniquement <span className="text-slate-500">(les anciens restent aux chatteuses)</span>
            </label>

            <label className="block text-[12px] text-slate-400 mt-3 mb-1">
              <b className="text-slate-300">Fans toujours suivis</b> — ils échappent à la case ci-dessus : le bot les garde même s'ils sont anciens. C'est ici que vont les fans que tu lui as confiés.
            </label>
            <input defaultValue={(S.toujoursSuivis || []).join(',')} onBlur={(e) => saveSettings({ toujoursSuivis: e.target.value }).then(() => flash('✓ enregistré'))} placeholder="ex : 8385657182,7166796270" className={input} />
          </Collapse>

          {/* 5. Personnalité (persona) — repliée ; état clair "définie" quand remplie */}
          <Collapse
            title={S.persona && S.persona.name ? 'Personnalité du modèle — ✓ définie' : 'Personnalité du modèle (à définir)'}
            help={S.persona && S.persona.name ? `Persona « ${S.persona.name} » enregistrée. Rien à faire — déplie seulement si tu veux l'ajuster.` : "Comment le bot parle et se comporte."}>
            <label className="block text-[12px] text-slate-400 mb-1">Fiche du modèle (perso, niche, façon de parler, limites, tarifs)</label>
            <textarea value={S.brief || ''} onChange={(e) => setS((x) => ({ ...x, brief: e.target.value }))} className={input + ' min-h-[90px] resize-y'} />
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <button onClick={() => saveSettings({ brief: S.brief || '' }).then(() => flash('✓ fiche enregistrée'))} className="rounded-xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] px-3 py-2 text-sm text-white">Enregistrer la fiche</button>
              <button onClick={genPersona} disabled={busy} className="rounded-xl px-3 py-2 text-sm font-semibold text-white flex items-center gap-1 disabled:opacity-60" style={{ background: 'linear-gradient(135deg,#3f7dff,#73a6ff)' }}><Sparkles size={14} /> {busy ? 'Génération…' : 'Régénérer la persona'}</button>
            </div>
            {S.persona && (
              <div className="mt-4 pt-4 border-t border-white/10 space-y-2">
                {[['name', 'Nom'], ['fiche', 'Identité / bio'], ['voix', 'Voix (comment elle écrit)'], ['exemples', 'Exemples de conversations'], ['tarifs', 'Tarifs']].map(([k, lab]) => (
                  <div key={k}>
                    <label className="block text-[12px] text-slate-400 mb-1">{lab}</label>
                    {k === 'name'
                      ? <input value={S.persona[k] || ''} onChange={(e) => setS((x) => ({ ...x, persona: { ...x.persona, [k]: e.target.value } }))} className={input} />
                      : <textarea value={S.persona[k] || ''} onChange={(e) => setS((x) => ({ ...x, persona: { ...x.persona, [k]: e.target.value } }))} className={input + ' min-h-[64px] resize-y'} />}
                  </div>
                ))}
                <button onClick={savePersona} className="rounded-xl px-3 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500">Enregistrer la persona</button>
              </div>
            )}
          </Collapse>

          <ScriptsAutorises botModel={modelId} flash={flash} onCoches={setNbCoches} />
          <ScriptFunnelSync botModel={modelId} flash={flash} onDone={() => loadModel(modelId)} />
          {/* Vente : funnel + rythme visibles UNIQUEMENT en mode Vente complète */}
          {S.mode === 'full' ? (<>
          {/* 30/09 (Marlon : « c'est la-bas que je pourrais rajouter des descriptions pour les etapes
              ou certains medias, n'est-ce pas ? pour aider le bot ») — OUI, et c'est meme critique.
              Le bot LIT ces descriptions pour deux choses (`verdictContenu` et `descriptionsDuModele`
              dans index.js) : savoir si ce qu'un fan reclame se trouve dans le prochain contenu payant,
              et ne jamais reciter la description a voix haute. Et le code se COUPE s'il n'y en a
              aucune : `if (!etapes.some((s) => s.contenuReel)) return ''`. Mesure du 30/09 : 0 etape
              decrite sur les 3 modeles — le bot vendait donc a l'aveugle. La section reste, et elle
              s'ouvre d'elle-meme tant qu'il manque des descriptions. */}
          {/* 12/10 : ce compteur ne porte QUE sur le funnel actif, alors que la carte du
              haut (BotVue) compte tous les funnels du modèle. Les deux chiffres ne se
              recoupaient pas et personne ne savait lequel lire : on dit ce qu'on compte.
              Un commentaire JSX ne peut PAS vivre entre deux attributs d'une balise :
              c'est ce qui a fait échouer la construction la première fois. */}
          <Collapse open={DECRITS.manque > 0}
            title={DECRITS.total === 0 ? 'Funnel de la 1ʳᵉ vente'
              : DECRITS.manque > 0 ? `Funnel de la 1ʳᵉ vente — ⚠ ${DECRITS.faits}/${DECRITS.total} étapes décrites dans CE funnel`
                : `Funnel de la 1ʳᵉ vente — ✓ ${DECRITS.total}/${DECRITS.total} décrites dans CE funnel`}
            help={DECRITS.manque > 0
              ? `Décris ce qu'il y a dans chaque média : sans ça le bot ne peut pas dire à un fan si ce qu'il réclame est dans le prochain contenu, et il ne le dira jamais. ${DECRITS.manque} étape(s) à décrire.`
              : "Ce que le bot envoie pour vendre, et ce qu'il y a dans chaque média. Le reste (chauffe/relationnel) vient de la persona."}>
            <div className="rounded-xl border border-indigo-500/20 bg-indigo-500/[0.05] p-3 mb-3 text-[12px] text-slate-300 leading-relaxed">
              <b className="text-white">Comment le bot procède :</b> il accueille et chauffe le fan (persona) → quand il est chaud, il envoie <b className="text-white">l'offre ci-dessous</b> (photo teaser optionnelle + message + <b className="text-white">lien Dropp</b>) → s'il n'a <b className="text-white">pas encore payé</b>, il relance/négocie → <b className="text-white">dès qu'il paie</b>, il passe la main à un chatteur.
              <div className="text-[11px] text-slate-500 mt-1">Astuce : mets une photo teaser sur l'étape pour donner envie avant le lien.</div>
            </div>
            {/* 30/09 (Marlon) : « je suis pas sûr de bien comprendre si je dois encore créer des funnels.
                Normalement ils sont déjà là. » — et il a raison : ses scénarios viennent de ses scripts
                ChatHub (carte « Scénarios que le bot peut utiliser »). Les boutons de création manuelle
                ne servaient qu'à semer le doute. Retirés. */}
            <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3 mb-2 text-[12px] text-slate-400">
              <b className="text-slate-200">Tu n'as rien à créer ici.</b> Les scénarios ci-dessous viennent
              automatiquement de tes <b className="text-slate-200">scripts ChatHub</b> : coche ceux que le bot peut
              utiliser dans la carte « Scénarios que le bot peut utiliser », plus haut. Celui marqué
              <span className="text-emerald-300"> actif</span> est celui qu'il essaie <b className="text-slate-200">en premier</b> avec un
              nouveau fan ; ensuite il enchaîne tout seul sur les autres qu'il n'a pas encore faits avec lui.
            </div>
            {(!FUN.funnels || FUN.funnels.length === 0) ? <p className="text-slate-500 text-sm">Aucun scénario pour ce modèle. Va cocher ses scripts dans « Scénarios que le bot peut utiliser », plus haut : ils apparaîtront ici tout seuls.</p> : (
              <div className="space-y-2">
                {FUN.funnels.map((f) => (
                  <div key={f.id} className="flex items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2">
                    <div className="text-sm text-white">{f.name} {f.id === FUN.activeFunnelId && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 ml-1">actif</span>} <span className="text-slate-500 text-[12px]">({(f.steps || []).length} étapes)</span></div>
                    <div className="flex items-center gap-1">
                      {f.id !== FUN.activeFunnelId && <button onClick={() => activateF(f.id)} className="text-[12px] px-2 py-1 rounded-lg border border-white/10 text-slate-200 hover:bg-white/[0.06] flex items-center gap-1"><Check size={12} /> Activer</button>}
                      <button onClick={() => editFunnel(f)} className="text-[12px] px-2 py-1 rounded-lg border border-white/10 text-slate-200 hover:bg-white/[0.06]">Modifier</button>
                      <button onClick={() => delF(f.id)} className="text-[12px] px-2 py-1 rounded-lg border border-red-500/30 text-red-300"><Trash2 size={12} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {editing && (
              <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
                <div><label className="block text-[12px] text-slate-400 mb-1">Nom du funnel</label>
                  <input value={editing.name} onChange={(e) => setEditing((x) => ({ ...x, name: e.target.value }))} className={input} /></div>
                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                  <input type="checkbox" checked={editing.enabled} onChange={(e) => setEditing((x) => ({ ...x, enabled: e.target.checked }))} className="accent-indigo-500" /> activer ce funnel (devient l'actif)
                </label>
                {editing.steps.map((s, i) => (
                  <div key={i} className="rounded-xl border border-white/10 bg-black/20 p-3 space-y-2">
                    <div className="flex items-center justify-between"><b className="text-sm text-white">Étape {i + 1}</b>
                      <div className="flex items-center gap-1">
                        <button onClick={() => mvStep(i, -1)} className="p-1 rounded border border-white/10 text-slate-300"><ChevronUp size={13} /></button>
                        <button onClick={() => mvStep(i, 1)} className="p-1 rounded border border-white/10 text-slate-300"><ChevronDown size={13} /></button>
                        <button onClick={() => rmStep(i)} className="p-1 rounded border border-red-500/30 text-red-300"><Trash2 size={13} /></button>
                      </div>
                    {/* 20/09 (Marlon) : « s'il a besoin que je lui fasse une description de ce qu'il y a
                        dans chaque video pour qu'il soit aide, on peut le faire ». Sans ca, le bot disait
                        oui a n'importe quelle demande precise et se contredisait a l'ouverture. */}
                    <div><label className="block text-[11px] text-slate-500 mb-1">Ce qu'il y a VRAIMENT dedans <span className="text-slate-600">(pour le bot, jamais envoyé au fan)</span></label>
                      <textarea data-testid="bot-contenu-reel" value={s.contenuReel || ''} onChange={(e) => setStep(i, 'contenuReel', e.target.value)}
                        className={input + ' min-h-[48px] resize-y'}
                        placeholder="ex : je me caresse les seins en lingerie noire, pas de visage, 2 min, pas de pénétration" /></div>
                    </div>
                    <div><label className="block text-[11px] text-slate-500 mb-1">Message de vente (1 ligne = 1 bulle)</label>
                      <textarea value={s.sellMessage} onChange={(e) => setStep(i, 'sellMessage', e.target.value)} className={input + ' min-h-[56px] resize-y'} /></div>
                    <div><label className="block text-[11px] text-slate-500 mb-1">Lien Dropp</label>
                      <input value={s.linkUrl} onChange={(e) => setStep(i, 'linkUrl', e.target.value)} className={input} placeholder="https://app.dropp.fans/…" /></div>
                    <div><label className="block text-[11px] text-slate-500 mb-1">Photo teaser <span className="text-slate-600">{s.photoFileId ? '✓ ajoutée' : '—'}</span></label>
                      <input type="file" accept="image/*" onChange={(e) => upPhoto(i, e.target.files[0])} className="text-[12px] text-slate-400" /></div>
                    <div><label className="block text-[11px] text-slate-500 mb-1">Message après-vente</label>
                      <textarea value={s.afterSaleMessage} onChange={(e) => setStep(i, 'afterSaleMessage', e.target.value)} className={input + ' min-h-[44px] resize-y'} /></div>
                  </div>
                ))}
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={addStep} className="rounded-xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] px-3 py-2 text-sm text-white flex items-center gap-1"><Plus size={14} /> Étape</button>
                  <button onClick={saveFunnel} className="rounded-xl px-3 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500">Enregistrer le funnel</button>
                  <button onClick={() => setEditing(null)} className="rounded-xl border border-white/10 px-3 py-2 text-sm text-slate-300">Fermer</button>
                </div>
              </div>
            )}
          </Collapse>

          {/* 7. Rythme + test — replié */}
          <Collapse title="Rythme de vente & test" help="Réglage fin : à quel point le bot chauffe avant de vendre, et un simulateur de paiement.">
            <label className="block text-[12px] text-slate-400 mb-1">Score mini avant de vendre (défaut 12)</label>
            <input defaultValue={S.sellThreshold ?? ''} onBlur={(e) => saveSettings({ sellThreshold: e.target.value }).then(() => flash('✓ rythme'))} placeholder="12" className={input + ' mb-2'} />
            <label className="block text-[12px] text-slate-400 mb-1">Messages du fan avant de vendre (défaut 6)</label>
            <input defaultValue={S.minMsgBeforeSell ?? ''} onBlur={(e) => saveSettings({ minMsgBeforeSell: e.target.value }).then(() => flash('✓ rythme'))} placeholder="6" className={input} />
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="text-[12px] text-slate-400 mb-2">Simuler un paiement (test, sans argent) — colle le lien de l'étape en cours.</div>
              <SimPay flash={flash} />
            </div>
          </Collapse>
          </>) : (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-[13px] text-slate-400">
              <b className="text-white">Accueil seulement, sans vendre</b> — le bot chauffe et <b className="text-white">passe la main au chatteur</b> avant toute vente. Pas de funnel ni de rythme à régler ici. Pour qu'il envoie lui-même le 1er lien, choisis <b className="text-white">« Accueil + 1ʳᵉ vente »</b> ou <b className="text-white">« gère tout »</b> plus haut.
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Chat de test : on écrit comme un fan, le bot répond via /ai/suggest (même IA claude-haiku
// + la persona du modèle), en respectant le mode choisi (accueil = relationnel, vente = sell).
// C'est un APERÇU pour régler le bot ; le test grandeur nature reste whitelist + Telegram.
function TestChat({ S, modelName }) {
  const [turns, setTurns] = useState([]);
  const [box, setBox] = useState('');
  const [phase, setPhase] = useState('accueil'); // mode vente : accueil | vente
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const isGreeter = S?.mode === 'greeter';
  const name = (S?.persona?.name) || modelName || 'Elle';

  const personaStr = () => {
    const p = S?.persona || {};
    const parts = [];
    if (p.name) parts.push('Nom : ' + p.name);
    if (p.fiche) parts.push('Identité : ' + p.fiche);
    if (p.voix) parts.push('Voix : ' + p.voix);
    if (p.exemples) parts.push('Exemples : ' + p.exemples);
    if (p.tarifs) parts.push('Tarifs : ' + p.tarifs);
    if (!parts.length && S?.brief) parts.push(S.brief);
    return parts.join('\n');
  };
  const pick = (r) => {
    if (!r) return null;
    if (Array.isArray(r.ideas) && r.ideas[0]) return r.ideas[0].text;
    if (Array.isArray(r.s) && r.s[0]) return r.s[0].t;
    if (Array.isArray(r.sequence) && r.sequence[0]) return r.sequence[0].text;
    if (Array.isArray(r.approaches) && r.approaches[0]) return (r.approaches[0].messages || [])[0];
    return null;
  };
  const send = async () => {
    const text = box.trim();
    if (!text || busy) return;
    setErr('');
    const next = [...turns, { from: 'fan', text }];
    setTurns(next); setBox(''); setBusy(true);
    const aiMode = isGreeter ? 'rel' : (phase === 'vente' ? 'sell' : 'rel');
    const transcript = next.map((t) => (t.from === 'fan' ? 'Fan' : name) + ' : ' + t.text).join('\n');
    try {
      const { data } = await api.post('/ai/suggest', { mode: aiMode, model_label: name, persona: personaStr(), transcript });
      const reply = pick(data?.result) || '(pas de réponse — réessaie)';
      setTurns((cur) => [...cur, { from: 'bot', text: reply }]);
    } catch (e) {
      const code = e?.response?.status;
      setErr(code === 429 ? 'Limite IA quotidienne atteinte — réessaie demain.' : (e?.response?.data?.detail || 'Assistant IA momentanément indisponible.'));
    } finally { setBusy(false); }
  };

  return (
    <Card title="🧪 Chat de test" help="Écris comme un fan → le bot répond avec la persona et le mode choisis. Aperçu pour régler ton bot (même IA que lui). Le test grandeur nature reste : whitelist + Telegram.">
      {!isGreeter && (
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[12px] text-slate-400">Phase :</span>
          <div className="flex items-center gap-1 rounded-lg border border-white/10 p-0.5 w-fit">
            {[['accueil', 'Accueil / chauffe'], ['vente', 'Vente']].map(([k, l]) => (
              <button key={k} onClick={() => setPhase(k)} className={`px-2.5 py-1 text-xs rounded-md transition-colors ${phase === k ? 'bg-indigo-500 text-white' : 'text-slate-400 hover:text-white'}`}>{l}</button>
            ))}
          </div>
        </div>
      )}
      <div className="rounded-xl border border-white/10 bg-black/20 p-3 h-[300px] overflow-y-auto flex flex-col gap-2">
        {turns.length === 0 && <div className="text-slate-500 text-sm m-auto text-center px-4">Écris un premier message comme si tu étais un fan 👇<br /><span className="text-[11px]">(ex : « salut, vu ton insta t'es magnifique »)</span></div>}
        {turns.map((t, i) => (
          <div key={i} className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${t.from === 'fan' ? 'self-end bg-indigo-500 text-white' : 'self-start bg-white/[0.06] text-slate-100'}`}>{t.text}</div>
        ))}
        {busy && <div className="self-start text-slate-500 text-xs px-1">{name} écrit…</div>}
      </div>
      {err && <div className="text-[12px] text-rose-300 mt-2">{err}</div>}
      <div className="flex items-center gap-2 mt-3">
        <input value={box} onChange={(e) => setBox(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') send(); }} placeholder="Message du fan…" className={input + ' flex-1'} />
        <button onClick={send} disabled={busy} className="rounded-xl px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60">Envoyer</button>
        <button onClick={() => { setTurns([]); setErr(''); }} className="rounded-xl border border-white/10 px-3 py-2 text-sm text-slate-300">Reset</button>
      </div>
    </Card>
  );
}

function SimPay({ flash }) {
  const [link, setLink] = useState('');
  const [amt, setAmt] = useState('10');
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!link.trim()) { flash('⚠ colle un lien'); return; }
    setBusy(true);
    try { const r = await bp.post('sim-pay', { linkId: link.trim(), amount: amt }); flash(r.ok ? `✓ simulé (${r.amount}€) — regarde Telegram` : '⚠ ' + (r.error || 'échec')); }
    catch (e) { flash('⚠ ' + (e?.response?.data?.detail || e.message)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-end gap-2 flex-wrap">
      <div className="flex-1 min-w-[200px]"><label className="block text-[11px] text-slate-500 mb-1">Lien Dropp (ou link_…)</label><input value={link} onChange={(e) => setLink(e.target.value)} className={input} /></div>
      <div className="w-24"><label className="block text-[11px] text-slate-500 mb-1">Montant €</label><input value={amt} onChange={(e) => setAmt(e.target.value)} className={input} /></div>
      <button onClick={run} disabled={busy} className="rounded-xl px-3 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 flex items-center gap-1 disabled:opacity-60"><PlayCircle size={15} /> Simuler</button>
    </div>
  );
}
